import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, runTransaction } from 'firebase/firestore';
import { db } from '../database/firebase';

/**
 * Add a new inventory log and update the corresponding product's count
 * @param {Object} logData - Log entry data
 */
export const addInventoryLog = async (logData) => {
    try {
        const { productId, type, quantity, date, note } = logData;
        const qtyNum = Number(quantity);

        return await runTransaction(db, async (transaction) => {
            // 1. Get current product data
            const productRef = doc(db, 'products', productId);
            const productSnap = await transaction.get(productRef);

            if (!productSnap.exists()) {
                throw new Error("Product does not exist!");
            }

            const productData = productSnap.data();
            const currentVal = Number(productData[type] || 0);
            const newVal = currentVal + qtyNum;

            // 2. Create the log entry
            const logsRef = collection(db, 'inventoryLogs');
            const newLogRef = doc(logsRef);
            transaction.set(newLogRef, {
                ...logData,
                createdAt: new Date().toISOString(),
                timestamp: new Date().toISOString()
            });

            // 3. Update product count
            transaction.update(productRef, {
                [type]: newVal,
                updatedAt: new Date().toISOString()
            });

            return newLogRef.id;
        });
    } catch (error) {
        console.error('Error adding inventory log:', error);
        throw error;
    }
};

/**
 * Fetch inventory logs with optional filters
 * @param {Object} filters - Optional filters (productId, type, startDate, endDate)
 */
export const getInventoryLogs = async (filters = {}) => {
    try {
        const logsRef = collection(db, 'inventoryLogs');
        // Simple query with only one orderBy to minimize index requirements
        let q = query(logsRef, orderBy('createdAt', 'desc'));

        const querySnapshot = await getDocs(q);
        let logs = [];
        querySnapshot.forEach((doc) => {
            logs.push({ id: doc.id, ...doc.data() });
        });

        // Apply filters in-memory to avoid composite index errors
        if (filters.productId) {
            logs = logs.filter(log => log.productId === filters.productId);
        }

        if (filters.type) {
            logs = logs.filter(log => log.type === filters.type);
        }

        return logs;
    } catch (error) {
        console.error('Error fetching inventory logs:', error);
        throw error;
    }
};

/**
 * Delete an inventory log and revert the product's count
 * @param {string} logId - Log ID
 */
export const deleteInventoryLog = async (logId) => {
    try {
        return await runTransaction(db, async (transaction) => {
            const logRef = doc(db, 'inventoryLogs', logId);
            const logSnap = await transaction.get(logRef);

            if (!logSnap.exists()) {
                throw new Error("Log does not exist!");
            }

            const logData = logSnap.data();
            const { productId, type, quantity } = logData;
            const qtyNum = Number(quantity);

            // Get product
            const productRef = doc(db, 'products', productId);
            const productSnap = await transaction.get(productRef);

            if (productSnap.exists()) {
                const productData = productSnap.data();
                const currentVal = Number(productData[type] || 0);
                const newVal = Math.max(0, currentVal - qtyNum);

                // Update product
                transaction.update(productRef, {
                    [type]: newVal,
                    updatedAt: new Date().toISOString()
                });
            }

            // Delete log
            transaction.delete(logRef);
        });
    } catch (error) {
        console.error('Error deleting inventory log:', error);
        throw error;
    }
};
